import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { api } from '../../api';
import { Dialog } from '../../components/Dialog';
import { fmtDate } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { isHourly } from '../empfehlung';
import { suche } from '../KundeWahl';
import type { Leistung } from '../LeistungDaten';
import { fehlerText, tarifFehler } from '../tarifHinweis';
import type { Kunde } from '../types';
import { VorschlagInput } from '../Vorschlaege';
import { dauerLesen, dauerText, stundenText, vonBis } from './dauer';
import { zeitenUrl, zeitUrl, type Zeit, type ZeitBody } from './ZeitDaten';

// „Zeit eintragen“ bzw. „Zeit ändern“: Tag, Dauer („1:30“ oder „1,5“, Chips, Hilfe von–bis), Kunde aus der Kundenliste
// (darf bis zum Berechnen fehlen), Tätigkeit und Einsatzort mit Vorschlägen, „Wer“ (nur wer alle Zeiten sieht, ab zwei
// Personen), „Nicht berechnen“. „Speichern und nächste“ lässt das Fenster offen (Tag, Kunde und Person bleiben).
// Prüfen macht der Server (Tag, Dauer, Kunde, Person, berechnet); hier nur, was sich sofort sagen lässt.

const DAUER_FEHLT = 'Bitte die Dauer eintragen, z. B. 1:30 oder 1,5.';
const CHIPS = [30, 60, 120, 240, 480];
const vorJahren = (tag: string, n: number) => `${Number(tag.slice(0, 4)) - n}${tag.slice(4)}`;

export interface ZeitVorschlaege {
  taetigkeiten: string[];
  objekte: string[];
}

export function ZeitDialog({
  open,
  firmId,
  zeit,
  vorgabe,
  kunden,
  leistungen,
  vorschlaege,
  personen,
  meId,
  today,
  onClose,
  onGespeichert,
  onLoeschen,
}: {
  open: boolean;
  firmId: string;
  // null: neu eintragen
  zeit: Zeit | null;
  // neu: Tag und Kunde vorbelegt (z. B. aus dem Filter der Liste)
  vorgabe?: { datum?: string; kundeId?: string | null };
  kunden: Kunde[];
  leistungen: Leistung[];
  vorschlaege: ZeitVorschlaege;
  // Personen des Teams (nur wer alle sieht und mindestens zwei Logins): Feld „Wer“
  personen: { id: string; name: string }[] | null;
  meId: string;
  today: string;
  onClose(): void;
  onGespeichert(z: Zeit, weiter: boolean): void;
  // Ändern: „Löschen“ (fragt die Seite)
  onLoeschen?(z: Zeit): void;
}) {
  const toast = useToast();
  const [datum, setDatum] = useState(today);
  const [dauer, setDauer] = useState('');
  const [kundeId, setKundeId] = useState<string | null>(null);
  const [taetigkeit, setTaetigkeit] = useState('');
  const [objekt, setObjekt] = useState('');
  const [nicht, setNicht] = useState(false);
  const [person, setPerson] = useState(meId);
  const [vb, setVb] = useState<{ von: string; bis: string; pause: string } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [dauerFehler, setDauerFehler] = useState(false);
  const [busy, setBusy] = useState(false);
  const dauerRef = useRef<HTMLInputElement>(null);

  // beim Öffnen: Stand der Zeit bzw. die Vorgaben
  useEffect(() => {
    if (!open) return;
    setDatum(zeit?.datum ?? vorgabe?.datum ?? today);
    setDauer(zeit ? dauerText(zeit.minuten) : '');
    setKundeId(zeit ? (zeit.kunde?.id ?? null) : (vorgabe?.kundeId ?? null));
    setTaetigkeit(zeit?.taetigkeit ?? '');
    setObjekt(zeit?.objekt ?? '');
    setNicht(zeit ? !zeit.berechnen : false);
    setPerson(zeit ? (zeit.person?.id ?? '') : meId);
    setVb(null);
    setFehler(null);
    setDauerFehler(false);
    setBusy(false);
    // nur beim Öffnen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, zeit?.id]);

  const minuten = dauerLesen(dauer);
  // Tätigkeiten: zuletzt benutzte und Leistungen in Std.
  const taetigkeiten = useMemo(
    () => [...new Set([...vorschlaege.taetigkeiten, ...leistungen.filter((l) => isHourly(l.unit)).map((l) => l.name)])],
    [vorschlaege.taetigkeiten, leistungen],
  );

  function rechnen(v: { von: string; bis: string; pause: string }) {
    setVb(v);
    const m = vonBis(v.von, v.bis, v.pause.trim() ? Number(v.pause.replace(',', '.')) : 0);
    if (m !== null) {
      setDauer(dauerText(m));
      setDauerFehler(false);
    }
  }

  async function speichern(weiter: boolean) {
    setFehler(null);
    if (minuten === null) {
      setDauerFehler(true);
      dauerRef.current?.focus();
      return;
    }
    const body: ZeitBody = {
      datum,
      minuten,
      kundeId,
      taetigkeit: taetigkeit.trim() || null,
      objekt: objekt.trim() || null,
      berechnen: !nicht,
      // „Wer“ nur, wenn es das Feld gibt und eine Person gewählt ist (früheres Teammitglied: bleibt)
      ...(personen && person ? { personId: person } : {}),
    };
    setBusy(true);
    try {
      const z = zeit ? await api.put<Zeit>(zeitUrl(zeit.id), body) : await api.post<Zeit>(zeitenUrl(firmId), body);
      toast(`Zeit gespeichert (${stundenText(z.minuten)})`);
      onGespeichert(z, weiter);
      if (weiter) {
        setDauer('');
        setTaetigkeit('');
        setObjekt('');
        setNicht(false);
        setVb(null);
        dauerRef.current?.focus();
      }
    } catch (err) {
      // Tarif: das Fenster „Mehr mit Billtoro“ zeigt es (dieses schließen, es kann nur eines offen sein)
      if (tarifFehler(err)) onClose();
      else setFehler(fehlerText(err));
    } finally {
      setBusy(false);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    void speichern(false);
  }

  return (
    <Dialog open={open} onClose={onClose} title={zeit ? 'Zeit ändern' : 'Zeit eintragen'} className="zt-dlg">
      <form className="zt-form" onSubmit={submit} noValidate>
        <div className="zt-zwei">
          <label className="field">
            <span>Tag</span>
            <input type="date" value={datum} min={vorJahren(today, 2)} max={today} required onChange={(e) => setDatum(e.target.value)} />
          </label>
          <label className="field">
            <span>Dauer</span>
            <input
              ref={dauerRef}
              value={dauer}
              inputMode="decimal"
              placeholder="z. B. 1:30 oder 1,5"
              autoFocus
              aria-invalid={dauerFehler}
              aria-describedby={dauerFehler ? 'zt-dauer-f' : undefined}
              onChange={(e) => {
                setDauer(e.target.value);
                setDauerFehler(false);
              }}
              onBlur={() => minuten !== null && setDauer(dauerText(minuten))}
            />
            {dauerFehler && (
              <span className="err" id="zt-dauer-f">
                {DAUER_FEHLT}
              </span>
            )}
          </label>
        </div>
        <div className="rt-chips zt-chips" aria-label="Dauer wählen">
          {CHIPS.map((m) => (
            <button
              key={m}
              type="button"
              className={`rt-chip${minuten === m ? ' on' : ''}`}
              aria-pressed={minuten === m}
              onClick={() => {
                setDauer(dauerText(m));
                setDauerFehler(false);
              }}
            >
              {dauerText(m)}
            </button>
          ))}
        </div>
        {vb ? (
          <div className="zt-vb">
            <div className="zt-drei">
              <label className="field">
                <span>von</span>
                <input type="time" value={vb.von} onChange={(e) => rechnen({ ...vb, von: e.target.value })} />
              </label>
              <label className="field">
                <span>bis</span>
                <input type="time" value={vb.bis} onChange={(e) => rechnen({ ...vb, bis: e.target.value })} />
              </label>
              <label className="field">
                <span>Pause (Minuten)</span>
                <input value={vb.pause} inputMode="numeric" placeholder="0" onChange={(e) => rechnen({ ...vb, pause: e.target.value })} />
              </label>
            </div>
            <p className="rt-hint zt-klein">Billtoro speichert nur die Dauer.</p>
          </div>
        ) : (
          <button type="button" className="linkbtn zt-vb-link" onClick={() => setVb({ von: '', bis: '', pause: '' })}>
            von–bis rechnen
          </button>
        )}

        <div className="field">
          <span id="zt-kunde-l">Kunde</span>
          <KundeSuche kunden={kunden} kundeId={kundeId} onChange={setKundeId} labelId="zt-kunde-l" />
          {!kundeId && <small>Zum Berechnen braucht die Zeit einen Kunden.</small>}
        </div>
        <label className="field">
          <span>Tätigkeit</span>
          <TextVorschlag value={taetigkeit} onChange={setTaetigkeit} vorschlaege={taetigkeiten} placeholder="z. B. Montage" maxLength={200} />
        </label>
        <label className="field">
          <span>Einsatzort oder Objekt (optional)</span>
          <TextVorschlag value={objekt} onChange={setObjekt} vorschlaege={vorschlaege.objekte} placeholder="z. B. Bürohaus Kehrwieder 7" maxLength={200} />
        </label>
        {personen && (
          <label className="field">
            <span>Wer</span>
            <select value={person} onChange={(e) => setPerson(e.target.value)}>
              {zeit && !zeit.person?.id && <option value="">{zeit.person?.name ?? 'Früheres Teammitglied'}</option>}
              {personen.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.id === meId ? `${p.name} (Sie)` : p.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="check zt-nicht">
          <input type="checkbox" checked={nicht} onChange={(e) => setNicht(e.target.checked)} />
          <span>Nicht berechnen (z. B. Kulanz)</span>
        </label>

        {fehler && <div className="notice bad">{fehler}</div>}
        <div className="dlg-actions zt-actions">
          {zeit && onLoeschen && (
            <button type="button" className="linkbtn rt-danger zt-weg" onClick={() => onLoeschen(zeit)}>
              Löschen
            </button>
          )}
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
          {!zeit && (
            <button type="button" className="btn" disabled={busy} onClick={() => void speichern(true)}>
              Speichern und nächste
            </button>
          )}
          <button type="submit" className="btn primary" disabled={busy}>
            Speichern
          </button>
        </div>
      </form>
    </Dialog>
  );
}

// Kunde aus der Kundenliste suchen (Name, Nummer oder Ort, wie in der Rechnung); neue Kunden legt man unter „Kunden“
// an. Gewählt: Name mit „ändern“.
export function KundeSuche({
  kunden,
  kundeId,
  onChange,
  labelId,
  label,
}: {
  kunden: Kunde[];
  kundeId: string | null;
  onChange(id: string | null): void;
  labelId?: string;
  label?: string;
}) {
  const [text, setText] = useState('');
  const gewaehlt = kundeId ? kunden.find((k) => k.id === kundeId) : undefined;
  const treffer = useMemo(() => suche(kunden, text), [kunden, text]);
  if (kundeId) {
    return (
      <span className="zt-kunde">
        <b>{gewaehlt?.name ?? 'Kunde nicht mehr in der Liste'}</b>
        {gewaehlt?.nummer != null && <span className="muted num"> Nr. {gewaehlt.nummer}</span>}
        <button
          type="button"
          className="linkbtn"
          aria-label={`${label ?? 'Kunde'}: anderen Kunden wählen`}
          onClick={() => {
            setText('');
            onChange(null);
          }}
        >
          ändern
        </button>
      </span>
    );
  }
  return (
    <VorschlagInput
      value={text}
      vorschlaege={treffer.map((k) => ({
        key: k.id!,
        label: (
          <>
            {k.nummer != null && <span className="kw-nr num">{k.nummer}</span>} {k.name}
          </>
        ),
        sub: [k.street, `${k.zip} ${k.city}`].filter((x) => x.trim()).join(', '),
      }))}
      onPick={(id) => onChange(id)}
      onChange={setText}
      inputProps={{
        ...(labelId ? { 'aria-labelledby': labelId } : { 'aria-label': label ?? 'Kunde' }),
        placeholder: kunden.length ? 'Kunde suchen: Name, Nummer oder Ort' : 'Noch keine Kunden in der Liste',
      }}
    />
  );
}

// Text mit Vorschlägen (zuletzt benutzt), höchstens 8, passend zum Getippten
export function TextVorschlag({
  value,
  onChange,
  vorschlaege,
  placeholder,
  maxLength,
  label,
  onBlur,
}: {
  value: string;
  onChange(v: string): void;
  vorschlaege: string[];
  placeholder?: string;
  maxLength?: number;
  label?: string;
  onBlur?(v: string): void;
}) {
  const t = value.trim().toLowerCase();
  const liste = vorschlaege.filter((v) => v.toLowerCase().includes(t) && v.toLowerCase() !== t).slice(0, 8);
  return (
    <VorschlagInput
      value={value}
      onChange={onChange}
      vorschlaege={liste.map((v) => ({ key: v, label: v }))}
      onPick={(v) => {
        onChange(v);
        onBlur?.(v);
      }}
      inputProps={{ placeholder, maxLength, ...(label ? { 'aria-label': label } : {}), onBlur: (e) => onBlur?.(e.target.value) }}
    />
  );
}

// „Zeit vom 20.10.2026 (1:30 Std.) löschen?“
export const loeschenFrage = (z: Pick<Zeit, 'datum' | 'minuten'>) => `Zeit vom ${fmtDate(z.datum)} (${stundenText(z.minuten)}) löschen?`;
